/**
 * Fasting API client and the formatting helpers its screens share.
 *
 * Lives here rather than in api/client.js for the same reason rhythmApi and
 * growthApi do: the feature carries helpers only it uses.
 *
 * Every time on a fasting sheet is shown in the PROGRAM's timezone, never the
 * viewer's. "6:00 PM – 6:00 PM" is what the organizer announced and what the
 * board agreed to; a member travelling west seeing "3:00 PM" would fast at the
 * wrong hour while believing the sheet. The zone is labelled where it matters.
 */
import { Auth } from 'aws-amplify';
import { api } from '../../api/client';
import { toPlainText } from '../../utils/richText';
export { COMMON_TIMEZONES } from '../rhythm/rhythmApi';

const BASE = process.env.REACT_APP_API_URL || '';

export const fastingApi = {
  list:    (cid)            => api.get(`/api/fasting/${cid}/programs`),
  create:  (cid, data)      => api.post(`/api/fasting/${cid}/programs`, data),
  get:     (cid, pid)       => api.get(`/api/fasting/${cid}/programs/${pid}`),
  update:  (cid, pid, data) => api.put(`/api/fasting/${cid}/programs/${pid}`, data),
  publish: (cid, pid)       => api.post(`/api/fasting/${cid}/programs/${pid}/publish`),
  archive: (cid, pid)       => api.post(`/api/fasting/${cid}/programs/${pid}/archive`),
  remove:  (cid, pid)       => api.delete(`/api/fasting/${cid}/programs/${pid}`),

  signUp:  (cid, pid, slotIds) => api.post(`/api/fasting/${cid}/programs/${pid}/signups`, { slotIds }),
  joinAll: (cid, pid)          => api.post(`/api/fasting/${cid}/programs/${pid}/signups`, { all: true }),
  leave:   (cid, pid, slotId)  => api.delete(`/api/fasting/${cid}/programs/${pid}/slots/${slotId}/signup`),

  // A public sign-up is an invitation: the fasts I signed up for as a guest, in
  // communities I am not in yet, and the one-tap join that answers one.
  invitations:    ()        => api.get('/api/fasting/invitations'),
  joinInvitation: (programId) => api.post(`/api/fasting/invitations/${programId}/join`),

  // Organizer
  assign:       (cid, pid, slotId, who) => api.post(`/api/fasting/${cid}/programs/${pid}/slots/${slotId}/assign`, who),
  removeSignup: (cid, pid, signupId)    => api.delete(`/api/fasting/${cid}/programs/${pid}/signups/${signupId}`),
};

/** The export is a file, so it is fetched with the token rather than through api.get's JSON parse. */
export async function downloadExport(cid, pid, filename) {
  const token = await Auth.currentSession().then((s) => s.getIdToken().getJwtToken()).catch(() => null);
  const res = await fetch(`${BASE}/api/fasting/${cid}/programs/${pid}/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  saveBlob(await res.blob(), filename || 'fast.csv');
}

// ── Public page (no account) ─────────────────────────────────────────────────
// Plain fetch: api.get would attach a signed-in visitor's token, and this page
// must behave the same for everybody.

async function publicRequest(method, path, body) {
  const res = await fetch(`${BASE}/api/fasting-public${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(json.error || `HTTP ${res.status}`);
    e.status = res.status;
    if (json.code) e.code = json.code;
    e.body = json;
    throw e;
  }
  return json;
}

export const fastingPublicApi = {
  get:    (slug, token) => publicRequest('GET', `/${encodeURIComponent(slug)}${token ? `?token=${encodeURIComponent(token)}` : ''}`),
  signUp: (slug, data)  => publicRequest('POST', `/${encodeURIComponent(slug)}/signup`, data),
  manage: (token)       => publicRequest('GET', `/manage/${encodeURIComponent(token)}`),
  setOptOut: (token, emailOptOut) => publicRequest('PUT', `/manage/${encodeURIComponent(token)}`, { emailOptOut }),
  cancelAll: (token) => publicRequest('DELETE', `/manage/${encodeURIComponent(token)}`),
  cancel: (token, slotId) => publicRequest('DELETE', `/manage/${encodeURIComponent(token)}/shifts/${encodeURIComponent(slotId)}`),
};

// The guest's manage token, remembered per sign-up page in this browser only.
// It is a convenience — the confirmation email carries the same link.
const TOKEN_KEY = (slug) => `ignite-fast-token:${slug}`;
export function readGuestToken(slug) {
  try { return localStorage.getItem(TOKEN_KEY(slug)) || null; } catch { return null; }
}
export function saveGuestToken(slug, token) {
  try { localStorage.setItem(TOKEN_KEY(slug), token); } catch { /* private mode */ }
}

// ── Formatting ───────────────────────────────────────────────────────────────

const localeFor = (lang) => (lang === 'es' ? 'es-US' : 'en-US');

export function formatTime(iso, tz, lang) {
  try {
    return new Intl.DateTimeFormat(localeFor(lang), { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  } catch { return ''; }
}

export function formatDay(iso, tz, lang, opts = { weekday: 'long', month: 'long', day: 'numeric' }) {
  try {
    return new Intl.DateTimeFormat(localeFor(lang), { timeZone: tz, ...opts }).format(new Date(iso));
  } catch { return ''; }
}

/** The calendar date of an instant in `tz`, as YYYY-MM-DD. */
export function dayKeyIn(iso, tz) {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(iso)); }
  catch { return String(iso).slice(0, 10); }
}

/** "EDT", "CST" — the program zone's short name at that instant. */
export function zoneAbbr(iso, tz, lang) {
  try {
    const part = new Intl.DateTimeFormat(localeFor(lang), { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date(iso)).find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch { return ''; }
}

/** "6:00 PM – 6:00 AM", with the end's weekday when it lands on another day. */
export function formatRange(slot, tz, lang) {
  const sameDay = dayKeyIn(slot.startsAt, tz) === dayKeyIn(slot.endsAt, tz);
  const end = sameDay
    ? formatTime(slot.endsAt, tz, lang)
    : `${formatDay(slot.endsAt, tz, lang, { weekday: 'short' })} ${formatTime(slot.endsAt, tz, lang)}`;
  return `${formatTime(slot.startsAt, tz, lang)} – ${end}`;
}

/**
 * One end of a shift, spelled out in full: { time: '6:00 PM', day: 'Tue, Oct 20' }.
 * Every shift shows both ends this way, so nobody has to work out which day
 * an overnight shift ends on from a group heading above it.
 */
export function formatPoint(iso, tz, lang) {
  return {
    time: formatTime(iso, tz, lang),
    day:  formatDay(iso, tz, lang, { weekday: 'short', month: 'short', day: 'numeric' }),
  };
}

/** Real elapsed hours — a shift across a DST change is honestly 23 or 25. */
export function durationHours(slot) {
  return Math.round((new Date(slot.endsAt).getTime() - new Date(slot.startsAt).getTime()) / 3600000);
}

/** The calendar tile on a day heading: { weekday: 'Tue', day: '20', month: 'Oct' }. */
export function dayTile(iso, tz, lang) {
  return {
    weekday: formatDay(iso, tz, lang, { weekday: 'short' }),
    day:     formatDay(iso, tz, lang, { day: 'numeric' }),
    month:   formatDay(iso, tz, lang, { month: 'short' }),
  };
}

/** Slots grouped under the local day they START on, in order. */
export function groupByDay(slots, tz) {
  const groups = [];
  let cur = null;
  for (const s of slots) {
    const key = dayKeyIn(s.startsAt, tz);
    if (!cur || cur.key !== key) { cur = { key, first: s.startsAt, slots: [] }; groups.push(cur); }
    cur.slots.push(s);
  }
  return groups;
}

/** 'past' | 'now' | 'upcoming', by the server's clock (`skew` = serverNow − clientNow). */
export function slotState(slot, skew = 0) {
  const now = Date.now() + skew;
  if (new Date(slot.endsAt).getTime() <= now) return 'past';
  if (new Date(slot.startsAt).getTime() <= now) return 'now';
  return 'upcoming';
}

export function isFull(slot) {
  return slot.capacity > 0 && slot.count >= slot.capacity;
}

// ── Add to calendar (.ics) ───────────────────────────────────────────────────

const icsStamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
function icsEscape(s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (c) => `\\${c}`);
}

/**
 * One VEVENT per shift, in UTC (so no VTIMEZONE block is needed and every
 * calendar app places it at the same instant). A 60-minute alarm rides along.
 */
export function buildIcs({ title, description: rich, url, shifts }) {
  // The description is rich HTML; a calendar app shows tags as text.
  const description = toPlainText(rich || '').trim();
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Ignite//Fasting//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH'];
  for (const s of shifts) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${s.id || s.slotId}@theigniteapp.com`,
      `DTSTAMP:${icsStamp(new Date().toISOString())}`,
      `DTSTART:${icsStamp(s.startsAt)}`,
      `DTEND:${icsStamp(s.endsAt)}`,
      `SUMMARY:${icsEscape(title)}`,
      ...(description || url ? [`DESCRIPTION:${icsEscape([description, url].filter(Boolean).join('\n\n'))}`] : []),
      ...(url ? [`URL:${url}`] : []),
      'BEGIN:VALARM', 'TRIGGER:-PT60M', 'ACTION:DISPLAY', `DESCRIPTION:${icsEscape(title)}`, 'END:VALARM',
      'END:VEVENT',
    );
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

export function downloadIcs(opts, filename = 'fast.ics') {
  saveBlob(new Blob([buildIcs(opts)], { type: 'text/calendar;charset=utf-8' }), filename);
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The public page's full URL, for the organizer's copy button and QR. */
export function publicUrl(slug) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://theigniteapp.com';
  return `${origin}/fast/${slug}`;
}

export const RELAY_BLOCK_HOURS = [1, 2, 3, 4, 6, 8, 12, 24];
export const REMINDER_HOURS    = [0, 1, 3, 12, 24];
